import { motion, type MotionValue, useTransform } from 'framer-motion';
import { slotOffset } from './orbit-math';

/** Industrial props that orbit on the podium deck, with their intrinsic sizes. */
const PROPS = [
    { name: 'offshore-platform', width: 640, height: 708 },
    { name: 'refinery', width: 640, height: 553 },
    { name: 'drilling-rig', width: 640, height: 640 },
    { name: 'processing-plant', width: 640, height: 434 },
    { name: 'tanker-ship', width: 640, height: 375 },
    { name: 'valve-skid', width: 640, height: 710 },
    { name: 'storage', width: 640, height: 444 },
    { name: 'tanker-truck', width: 640, height: 277 },
    { name: 'crane', width: 640, height: 427 },
    { name: 'port-crane', width: 640, height: 632 },
] as const;

const ORBIT_RATIO = 0.9;
const MIN_SCALE = 0.62;
const SIZE_FACTOR = 0.7;
const MAX_ASPECT = 1.2;

interface PropProps {
    slot: number;
    index: number;
    activeIndex: number;
    position: MotionValue<number>;
    rx: number;
    flatten: number;
    itemAngle: number;
    slotCount: number;
    size: number;
    layer: 'back' | 'front';
    assetBase: string;
}

function OrbitProp({ slot, index, activeIndex, position, rx, flatten, itemAngle, slotCount, size, layer, assetBase }: PropProps) {
    const orbitX = rx * ORBIT_RATIO;
    const orbitY = orbitX * flatten;
    const prop = PROPS[((index % PROPS.length) + PROPS.length) % PROPS.length];
    const scale = Math.min(size / Math.sqrt(prop.width * prop.height), (size * MAX_ASPECT) / prop.width, (size * MAX_ASPECT) / prop.height) * SIZE_FACTOR;

    const pose = useTransform(position, (latest) => {
        const angle = slotOffset(slot, latest, slotCount) * itemAngle;

        return { x: orbitX * Math.sin(angle), y: orbitY * Math.cos(angle), depth: Math.cos(angle) };
    });
    const x = useTransform(pose, (p) => p.x);
    const y = useTransform(pose, (p) => p.y);
    const propScale = useTransform(pose, (p) => MIN_SCALE + 0.38 * Math.max(0, p.depth));
    const opacity = useTransform(pose, (p) => {
        if (layer === 'front') {
            return p.depth >= 0 ? 1 : 0;
        }

        return p.depth > 0 ? 0 : Math.min(1, Math.max(0, (p.depth + 0.78) / 0.3));
    });
    const zIndex = useTransform(pose, (p) => Math.round(p.depth * 100));

    return (
        <motion.div
            className={`wop-orbit__prop${index === activeIndex ? ' is-active' : ''}`}
            style={{ x, y, scale: propScale, opacity, zIndex, width: prop.width * scale, height: prop.height * scale }}
            transformTemplate={({ x: tx, y: ty, scale: s }) => `translate(-50%, 0) translate(${tx}, ${ty}) scale(${s})`}
            aria-hidden="true"
        >
            <img src={`${assetBase}/props/${prop.name}.webp`} alt="" draggable={false} decoding="async" />
        </motion.div>
    );
}

interface PropLayerProps extends Omit<PropProps, 'slot' | 'index'> {
    itemCount: number;
}

export function PropLayer({ itemCount, ...props }: PropLayerProps) {
    if (itemCount === 0) {
        return null;
    }

    return (
        <div className={`wop-orbit__props${props.layer === 'back' ? ' wop-orbit__props--back' : ''}`} aria-hidden="true">
            {Array.from({ length: props.slotCount }, (_, slot) => (
                <OrbitProp key={slot} slot={slot} index={slot % itemCount} {...props} />
            ))}
        </div>
    );
}
